import { useTranslation } from 'react-i18next';
import * as yup from 'yup';

import { BillableHoursProposalData } from '~/data/proposals';
import {
  CommonEmployeeProposalFormData,
  mapDefaultDataUtils,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/Common';
import { convertToDateOrNull } from '~/utils/helpers/date';

type CustomFields = {
  [key: string]: string | string[];
};

export type BillableHoursProposalFormData = CommonEmployeeProposalFormData & {
  hoursType: string;
  hours: number;
  total: number;
  projectType: string;
  paymentPeriodStartDate: Date | null;
  paymentPeriodEndDate?: Date | null;
  paymentDate: Date | null;
  info: string;
  fBillableHoursProposalCustomFields?: CustomFields;
};
export const mapDefaultData = (
  targetEmploymentId: number,
  proposalData?: BillableHoursProposalData,
  defaultResponsibleHrId?: number,
  defaultBillableType?: string,
): BillableHoursProposalFormData => {
  const { defaultToEmptyString, defaultResponsibleHR, defaultToNumber } =
    mapDefaultDataUtils();

  return {
    employmentId: defaultToEmptyString(
      proposalData?.employmentFK || targetEmploymentId,
    ),
    hoursType: defaultToEmptyString(
      proposalData?.billableHoursTypeFK || defaultBillableType,
    ),
    hours: defaultToNumber(proposalData?.hours, 0),
    total: defaultToNumber(proposalData?.total, 0),
    projectType: defaultToEmptyString(proposalData?.billableHoursProjectTypeFK),
    paymentPeriodStartDate: convertToDateOrNull(
      proposalData?.paymentPeriodStartDate,
    ),
    paymentPeriodEndDate: convertToDateOrNull(
      proposalData?.paymentPeriodEndDate,
    ),
    paymentDate: convertToDateOrNull(proposalData?.paymentDate),
    info: defaultToEmptyString(proposalData?.info),
    responsibleHR: defaultResponsibleHR(
      defaultResponsibleHrId,
      defaultResponsibleHrId,
    ),
  };
};

export const useValidationSchema = (isEmploymentIdRequired: boolean) => {
  const { t } = useTranslation();

  return yup.object().shape<BillableHoursProposalFormData>({
    employmentId: isEmploymentIdRequired
      ? yup.string().required(t('fieldIsRequired'))
      : yup.string().default(''),
    hoursType: yup.string().required(t('fieldIsRequired')),
    hours: yup
      .number()
      .typeError(t('laValueNotValidNumber'))
      .required(t('fieldIsRequired')),
    total: yup
      .number()
      .typeError(t('laValueNotValidNumber'))
      .required(t('fieldIsRequired')),
    projectType: yup.string(),
    paymentPeriodStartDate: yup
      .date()
      .nullable()
      .required(t('fieldIsRequired')),
    paymentPeriodEndDate: yup.date().nullable(),
    paymentDate: yup.date().nullable().required(t('fieldIsRequired')),
    info: yup.string(),
    responsibleHR: yup.number().required(t('fieldIsRequired')),
  });
};
